import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Cpu, Zap, Home as HomeIcon, MapPin, Users, Wrench, Award } from "lucide-react";
import usePageTitle from "@/lib/usePageTitle";

const PARTNERS = ["Godrej", "HomeMate", "Wipro", "Atomberg", "Havells", "Glavna"];
const CITIES = ["Kolkata", "Gurugram", "Bhubaneswar", "Agartala"];

const values = [
    { icon: HomeIcon, title: "Retrofit-first", body: "We design around the appliances you already own. Nothing gets thrown away." },
    { icon: Zap, title: "Fast to install", body: "Most homes go smart in a single hour — no rewiring, no downtime." },
    { icon: ShieldCheck, title: "Built to last", body: "1-year warranty on everything we install, extendable, plus lifetime service." },
    { icon: Cpu, title: "Standards-based", body: "Works with Alexa, Google Home and Siri — future-proof by design." },
];

const stats = [
    { k: "36+", v: "Months live" },
    { k: "4", v: "Cities served" },
    { k: "1 hr", v: "Avg install time" },
    { k: "₹17,999", v: "Starting price" },
];

export default function About() {
    usePageTitle("About GoSmartNest");
    return (
        <div className="bg-white" data-testid="about-page">
            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
                <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-radial-cyan pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-16 lg:pt-20 lg:pb-24">
                    <div className="max-w-3xl">
                        <div className="text-[11px] tracking-[0.2em] uppercase text-[#1BB6F0] font-semibold">About GoSmartNest</div>
                        <motion.h1
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0B1440] leading-[1.05]"
                            data-testid="about-title"
                        >
                            Smart homes shouldn't be complicated —<br />
                            <span className="text-brand-gradient">or reserved for the rich.</span>
                        </motion.h1>
                        <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
                            GoSmartNest was built on a stubborn belief: modern Indian homes deserve modern technology
                            without a full renovation. We retrofit the appliances you already love — lights, fans, ACs,
                            curtains, locks — into one connected, intelligent home you can control from a switch, an app
                            or your voice.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/contact" data-testid="about-cta-consult" className="inline-flex items-center gap-1.5 bg-[#1B2E7A] hover:bg-[#13215A] text-white rounded-full px-7 py-3.5 font-medium transition-colors">
                                Get Free Consultation
                            </Link>
                            <Link to="/experience-smart-living" data-testid="about-cta-experience" className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-[#1BB6F0] text-[#1B2E7A] rounded-full px-7 py-3.5 font-medium transition-colors">
                                Experience Smart Living →
                            </Link>
                        </div>
                    </div>

                    {/* Stats */}
                    <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {stats.map((s, i) => (
                            <div key={i} className="rounded-3xl border border-slate-200 bg-white p-6" data-testid={`about-stat-${i}`}>
                                <div className="font-mono text-3xl sm:text-4xl font-semibold text-[#1B2E7A]">{s.k}</div>
                                <div className="mt-1 text-xs uppercase tracking-widest text-slate-500 font-semibold">{s.v}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Mission */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <div className="text-[11px] tracking-[0.2em] uppercase text-[#1BB6F0] font-semibold">Our mission</div>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-[#1B2E7A] leading-tight">
                            Three words we build every day around.
                        </h2>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {[
                                { t: "Simple", d: "One app. One team. Zero jargon." },
                                { t: "Affordable", d: "Premium experience without premium pricing." },
                                { t: "Reliable", d: "Standards-based hardware and a lifetime service promise." },
                            ].map((v, i) => (
                                <div key={i} className="rounded-3xl border border-slate-200 p-7 bg-white hover:border-[#1BB6F0]/60 hover:shadow-xl hover:shadow-[#1B2E7A]/10 transition-[border-color,box-shadow] duration-300">
                                    <div className="w-10 h-10 rounded-full bg-[#E8F8FE] flex items-center justify-center text-[#1BB6F0] font-mono text-sm font-semibold">0{i + 1}</div>
                                    <div className="mt-5 text-xl font-semibold text-[#1B2E7A]">{v.t}</div>
                                    <div className="mt-2 text-sm text-slate-600 leading-relaxed">{v.d}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="bg-[#0B1440] text-white py-24">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <div className="max-w-2xl">
                        <div className="text-[11px] tracking-[0.2em] uppercase text-[#1BB6F0] font-semibold">What guides us</div>
                        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
                            Four principles behind every install.
                        </h2>
                    </div>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {values.map((v, i) => (
                            <div key={i} className="p-7 rounded-3xl bg-white/[0.04] border border-white/10 hover:border-[#1BB6F0]/60 transition-colors" data-testid={`value-${i}`}>
                                <div className="w-11 h-11 rounded-2xl bg-[#1BB6F0]/15 text-[#1BB6F0] flex items-center justify-center">
                                    <v.icon className="w-5 h-5" strokeWidth={2} />
                                </div>
                                <div className="mt-5 text-lg font-semibold">{v.title}</div>
                                <div className="mt-2 text-sm text-white/70 leading-relaxed">{v.body}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Partners */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-5">
                        <div className="text-[11px] tracking-[0.2em] uppercase text-[#1BB6F0] font-semibold">Trusted hardware</div>
                        <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-[#1B2E7A] leading-tight">
                            Sourced from India's most reliable brands.
                        </h2>
                        <p className="mt-4 text-slate-600 max-w-md">
                            We don't build black-box devices. We source proven IoT and control hardware from manufacturers
                            with robust Indian supply chains — so your smart home stays supported for years.
                        </p>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {PARTNERS.map((p, i) => (
                                <div key={p} className="h-24 flex items-center justify-center rounded-2xl bg-white border border-slate-200 text-lg font-semibold text-[#1B2E7A] tracking-tight" data-testid={`partner-${i}`}>
                                    {p}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Cities */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
                <div className="rounded-[32px] bg-gradient-to-br from-[#1BB6F0] to-[#1E90FF] text-white p-8 sm:p-14">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                        <div className="lg:col-span-7">
                            <div className="text-[11px] tracking-[0.2em] uppercase text-white/80 font-semibold flex items-center gap-2">
                                <MapPin className="w-3.5 h-3.5" /> Where we serve
                            </div>
                            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
                                4 cities. And growing.
                            </h2>
                            <div className="mt-6 flex flex-wrap gap-2">
                                {CITIES.map((c) => (
                                    <span key={c} className="px-4 py-2 rounded-full bg-white/15 border border-white/20 backdrop-blur-sm text-sm font-medium">{c}</span>
                                ))}
                            </div>
                        </div>
                        <div className="lg:col-span-5 flex lg:justify-end">
                            <Link to="/contact" data-testid="about-city-cta" className="inline-flex items-center gap-1.5 bg-white text-[#1B2E7A] rounded-full px-7 py-3.5 font-medium hover:bg-white/90 transition-colors">
                                Check availability in your city
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Team callout */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[
                        { icon: Users, t: "Small, obsessive team", d: "Engineers, designers and installers who actually use the product at home." },
                        { icon: Wrench, t: "In-house installs", d: "No subcontractors. Every install is done by our own certified technicians." },
                        { icon: Award, t: "Lifetime service promise", d: "Beyond warranty, we're your go-to for anything smart-home in your city." },
                    ].map((v, i) => (
                        <div key={i} className="rounded-3xl border border-slate-200 bg-white p-7">
                            <div className="w-10 h-10 rounded-xl bg-[#E8F8FE] flex items-center justify-center text-[#1BB6F0]"><v.icon className="w-4.5 h-4.5" /></div>
                            <div className="mt-4 text-lg font-semibold text-[#1B2E7A]">{v.t}</div>
                            <div className="mt-2 text-sm text-slate-600 leading-relaxed">{v.d}</div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
